import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { env } from '@/lib/env';

export const Route = createFileRoute('/_app/operator/users')({
  staticData: { crumb: 'Użytkownicy' },
  component: OperatorUsersPage,
});

interface OperatorUser {
  id: string;
  email: string;
  full_name: string | null;
  is_operator: boolean;
  tenant_count: number;
  last_login_at: number | null;
  created_at: number;
}

function OperatorUsersPage() {
  const users = useQuery({
    queryKey: ['operator', 'users'],
    queryFn: async (): Promise<OperatorUser[]> => {
      const res = await fetch(`${env.apiBaseUrl}/api/operator/users`, {
        credentials: 'include',
      });
      if (!res.ok) {
        // Endpoint may not be implemented yet — degrade gracefully.
        if (res.status === 404) return [];
        throw new Error('users_load_failed');
      }
      return (await res.json()) as OperatorUser[];
    },
    retry: 0,
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Użytkownicy
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Wszystkie konta admin we wszystkich klinikach. Operatorzy są
          oznaczeni odrębnie.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{users.data ? `${users.data.length} kont` : 'Ładowanie…'}</CardTitle>
          <CardDescription>
            Sortowanie po dacie ostatniego logowania. Endpoint
            <code className="mx-1 rounded bg-surface px-1 py-0.5 text-xs">
              /api/operator/users
            </code>
            jest jeszcze w budowie — lista może być pusta.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {users.isPending && (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          )}
          {users.isError && (
            <p className="text-sm text-error">
              Nie udało się pobrać listy użytkowników.
            </p>
          )}
          {users.data && users.data.length === 0 && !users.isError && (
            <p className="text-sm text-text-muted">
              Brak użytkowników do wyświetlenia (endpoint nie jest jeszcze wystawiony).
            </p>
          )}
          {users.data && users.data.length > 0 && (
            <ul className="divide-y divide-border">
              {users.data.map((u) => (
                <li key={u.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-medium text-text">{u.full_name ?? u.email}</p>
                    <p className="text-xs text-text-muted">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-text-subtle">
                    {u.is_operator && <Badge variant="soft">operator</Badge>}
                    <span>{u.tenant_count} tenant(s)</span>
                    <span>
                      {u.last_login_at
                        ? new Date(u.last_login_at * 1000).toLocaleDateString('pl-PL')
                        : 'nigdy'}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
